/* eslint-disable max-lines, max-statements */

interface RenderState {
  colors: ThemeColors;
  context: CanvasRenderingContext2D;
  height: number;
  width: number;
}

interface MistLayer {
  color: string;
  opacity: number;
  radius: number;
  x: number;
  y: number;
}

interface WaveLayer {
  amplitude: number;
  baseY: number;
  blur: number;
  centerX: number;
  color: string;
  crestColor: string;
  crestOpacity: number;
  opacity: number;
  phase: number;
  spread: number;
  time: number;
}

interface ThemeColors {
  mistBlue: string;
  mistCool: string;
  mistRose: string;
  mistWarm: string;
  waveFive: string;
  waveFour: string;
  waveOne: string;
  waveSheen: string;
  waveThree: string;
  waveTwo: string;
}

type WavePoint = [number, number];
type WavePreset = [
  colorName: string,
  baseYRatio: number,
  amplitudeRatio: number,
  spreadRatio: number,
  opacity: number,
  blur: number,
  phase: number,
  crestOpacity: number,
  speed: number,
  offset: number
];

function readThemeColors(): ThemeColors {
  const styles = getComputedStyle(document.documentElement);
  const read = (name: string) => styles.getPropertyValue(name).trim();

  return {
    mistBlue: read('--hero-mist-blue'),
    mistCool: read('--hero-mist-cool'),
    mistRose: read('--hero-mist-rose'),
    mistWarm: read('--hero-mist-warm'),
    waveFive: read('--hero-wave-five'),
    waveFour: read('--hero-wave-four'),
    waveOne: read('--hero-wave-one'),
    waveSheen: read('--hero-wave-sheen'),
    waveThree: read('--hero-wave-three'),
    waveTwo: read('--hero-wave-two')
  };
}

const wavePresets: WavePreset[] = [
  ['--hero-wave-one', 1.03, 0.42, 0.34, 0.34, 10, 0.4, 0.08, 0.052, 0.08],
  ['--hero-wave-two', 1.12, 0.64, 0.31, 0.36, 11, 1.8, 0.1, 0.058, 0.36],
  ['--hero-wave-three', 1.04, 0.47, 0.3, 0.34, 10, 3.1, 0.08, 0.048, 0.62],
  ['--hero-wave-five', 1.28, 0.46, 0.42, 0.26, 9, 4.2, 0.07, 0.055, 0.84],
  ['--hero-wave-four', 1.2, 0.4, 0.39, 0.24, 9, 5, 0.07, 0.05, 0.2]
];

function getTravelingCenter(width: number, time: number, speed: number, offset: number): number {
  const start = -width * 0.9;
  const travel = width * 2.8;
  const distance = (time * width * speed + offset * travel) % travel;

  return start + distance;
}

function drawMist(state: RenderState, layer: MistLayer): void {
  const {context} = state;
  const {color, opacity, radius, x, y} = layer;
  const gradient = context.createRadialGradient(x, y, 0, x, y, radius);

  gradient.addColorStop(0, color);
  gradient.addColorStop(0.55, color);
  gradient.addColorStop(1, 'transparent');

  context.save();
  context.globalAlpha = opacity;
  context.fillStyle = gradient;
  context.fillRect(x - radius, y - radius, radius * 2, radius * 2);
  context.restore();
}

function getWavePoints(state: RenderState, layer: WaveLayer): WavePoint[] {
  const {height, width} = state;
  const {amplitude, baseY, centerX, phase, spread, time} = layer;
  const left = -width * 0.35;
  const right = width * 1.35;
  const points: WavePoint[] = [];

  for (let step = 0; step <= 96; step += 1) {
    const progress = step / 96;
    const x = left + (right - left) * progress;
    const primaryDistance = (x - centerX) / spread;
    const shoulderDistance = (x - centerX - spread * 0.72) / (spread * 1.18);
    const primary = Math.exp(-primaryDistance * primaryDistance);
    const shoulder = Math.exp(-shoulderDistance * shoulderDistance);
    const ripple = Math.sin(progress * Math.PI * 2.4 + time * 0.52 + phase) * height * 0.01;
    const drift = Math.cos(progress * Math.PI * 1.35 + time * 0.34 + phase) * height * 0.007;
    const y = baseY - amplitude * primary - amplitude * 0.22 * shoulder + ripple + drift;

    points.push([x, y]);
  }

  return points;
}

function traceWaveCurve(context: CanvasRenderingContext2D, points: WavePoint[]): void {
  points.forEach(([x, y], index) => {
    if (index === 0) {
      context.moveTo(x, y);
    } else {
      context.lineTo(x, y);
    }
  });
}

function drawWaveFill(state: RenderState, layer: WaveLayer, points: WavePoint[]): void {
  const {context, height, width} = state;
  const {blur, color, opacity} = layer;
  const left = -width * 0.35;
  const right = width * 1.35;
  const bottom = height * 1.18;

  context.save();
  context.globalAlpha = opacity;
  context.filter = `blur(${blur}px)`;
  context.fillStyle = color;
  context.beginPath();
  context.moveTo(left, bottom);

  points.forEach(([x, y]) => {
    context.lineTo(x, y);
  });

  context.lineTo(right, bottom);
  context.closePath();
  context.fill();
  context.restore();
}

function drawWaveCrest(state: RenderState, layer: WaveLayer, points: WavePoint[]): void {
  const {context, height} = state;
  const {blur, crestColor, crestOpacity} = layer;

  context.save();
  context.globalAlpha = crestOpacity;
  context.filter = `blur(${Math.max(5, blur * 0.55)}px)`;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.lineWidth = Math.max(2, height * 0.006);
  context.strokeStyle = crestColor;
  context.beginPath();
  traceWaveCurve(context, points);
  context.stroke();
  context.restore();
}

function drawWave(state: RenderState, layer: WaveLayer): void {
  const points = getWavePoints(state, layer);

  drawWaveFill(state, layer, points);
  drawWaveCrest(state, layer, points);
}

function getMistLayers(state: RenderState, time: number): MistLayer[] {
  const {colors, height, width} = state;

  return [
    {
      x: width * 0.18 + Math.sin(time * 0.22) * width * 0.035,
      y: height * 0.24,
      radius: width * 0.5,
      color: colors.mistCool,
      opacity: 0.2
    },
    {
      x: width * 0.82 + Math.cos(time * 0.2) * width * 0.035,
      y: height * 0.36,
      radius: width * 0.42,
      color: colors.mistWarm,
      opacity: 0.15
    },
    {
      x: width * 0.5 + Math.sin(time * 0.26) * width * 0.04,
      y: height * 0.84,
      radius: width * 0.54,
      color: colors.mistRose,
      opacity: 0.13
    },
    {
      x: width * 0.86 + Math.cos(time * 0.3) * width * 0.04,
      y: height * 0.82,
      radius: width * 0.36,
      color: colors.mistBlue,
      opacity: 0.13
    }
  ];
}

function getWaveColor(colors: ThemeColors, colorName: string): string {
  if (colorName === '--hero-wave-two') {
    return colors.waveTwo;
  }
  if (colorName === '--hero-wave-three') {
    return colors.waveThree;
  }
  if (colorName === '--hero-wave-four') {
    return colors.waveFour;
  }
  if (colorName === '--hero-wave-five') {
    return colors.waveFive;
  }

  return colors.waveOne;
}

function getWaveLayers(state: RenderState, time: number): WaveLayer[] {
  const {colors, height, width} = state;

  return wavePresets.map(([
    colorName,
    baseYRatio,
    amplitudeRatio,
    spreadRatio,
    opacity,
    blur,
    phase,
    crestOpacity,
    speed,
    offset
  ]) => ({
    centerX: getTravelingCenter(width, time, speed, offset),
    baseY: height * baseYRatio,
    amplitude: height * amplitudeRatio,
    spread: width * spreadRatio,
    color: getWaveColor(colors, colorName),
    opacity,
    blur,
    time,
    phase,
    crestColor: colors.waveSheen,
    crestOpacity
  }));
}

const targetFrameDuration = 1000 / 30;

export function startHeroWaveAnimation(canvas: HTMLCanvasElement): () => void {
  const context = canvas.getContext('2d', {
    alpha: true,
    desynchronized: true
  });

  if (!context) {
    return () => undefined;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let animationFrame = 0;
  let colors = readThemeColors();
  let width = 0;
  let height = 0;
  let visible = false;
  let lastDrawTime = -targetFrameDuration;

  const getRenderState = (): RenderState => ({
    colors,
    context,
    height,
    width
  });

  const cancelFrame = () => {
    if (animationFrame) {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    }
  };

  const shouldAnimate = () =>
    visible && !reducedMotion.matches && !document.hidden;

  const requestFrame = () => {
    if (!animationFrame && shouldAnimate()) {
      animationFrame = window.requestAnimationFrame(draw);
    }
  };

  const resizeCanvas = () => {
    const rect = canvas.getBoundingClientRect();
    const deviceScale = Math.min(window.devicePixelRatio || 1, 1.25);

    width = rect.width;
    height = rect.height;
    canvas.width = Math.max(1, Math.floor(width * deviceScale));
    canvas.height = Math.max(1, Math.floor(height * deviceScale));
    context.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);
  };

  const draw = (timestamp: number) => {
    animationFrame = 0;

    if (timestamp - lastDrawTime < targetFrameDuration) {
      requestFrame();

      return;
    }

    const time = timestamp / 1000;
    const renderState = getRenderState();

    lastDrawTime = timestamp;
    context.clearRect(0, 0, width, height);
    getMistLayers(renderState, time).forEach((layer) => drawMist(renderState, layer));
    getWaveLayers(renderState, time).forEach((layer) => drawWave(renderState, layer));

    requestFrame();
  };

  const drawStaticFrame = () => {
    lastDrawTime = -targetFrameDuration;
    draw(performance.now());
  };

  resizeCanvas();

  const handleResize = () => {
    resizeCanvas();
    drawStaticFrame();
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      cancelFrame();
    } else {
      drawStaticFrame();
      requestFrame();
    }
  };

  const heroObserver = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;

    if (visible) {
      drawStaticFrame();
      requestFrame();
    } else {
      cancelFrame();
    }
  }, {rootMargin: '160px 0px'});

  const themeObserver = new MutationObserver(() => {
    colors = readThemeColors();
    drawStaticFrame();
  });

  heroObserver.observe(canvas);
  themeObserver.observe(document.documentElement, {
    attributeFilter: ['data-theme'],
    attributes: true
  });
  window.addEventListener('resize', handleResize);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    cancelFrame();
    heroObserver.disconnect();
    themeObserver.disconnect();
    window.removeEventListener('resize', handleResize);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}
